import { useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { AccountSettingsPage, PlanUsageCard } from '@roselabs-io/saas-shell'
import type { AccountSettingsPageProps } from '@roselabs-io/saas-shell'
import { useAuth, authApi } from '../lib/auth'
import { usersApi } from '../lib/users'
import { billingApi } from '../api/billing'
import type { UsageResponse } from '../api/billing'
import { teamsApi } from '../api/teams'

// Transform Falcon usage data to PlanUsageCard format
function transformFalconUsage(usageData: UsageResponse) {
  // Build usage metrics array, handling unlimited values
  const usageMetrics = []

  // Uptime monitors
  if (usageData.max_uptime_monitors !== null) {
    usageMetrics.push({
      label: 'Uptime Monitors',
      value: usageData.current_uptime_monitors,
      max: usageData.max_uptime_monitors,
      showMax: true,
    })
  } else {
    usageMetrics.push({ label: 'Uptime Monitors', value: usageData.current_uptime_monitors })
  }

  // Cron monitors
  if (usageData.max_cron_monitors !== null) {
    usageMetrics.push({
      label: 'Cron Monitors',
      value: usageData.current_cron_monitors,
      max: usageData.max_cron_monitors,
      showMax: true,
    })
  } else {
    usageMetrics.push({ label: 'Cron Monitors', value: usageData.current_cron_monitors })
  }

  // Retention days
  usageMetrics.push({ label: 'Retention', value: usageData.retention_days, max: undefined })

  return {
    primaryUsage: {
      current: usageData.errors_this_month,
      max: usageData.max_errors_monthly ?? Infinity,
      unit: 'errors',
      showUsed: true,
    },
    usageMetrics,
  }
}

export default function SettingsPage() {
  const { teamId } = useParams<{ teamId: string }>()
  const { user, logout, updateConsent } = useAuth()

  if (!teamId || !user) {
    return null
  }

  const api = {
    // Users API
    getProfile: usersApi.getProfile,
    updateProfile: usersApi.updateProfile,
    setPassword: async (data: { password: string }) => {
      await usersApi.setPassword(data)
    },
    disconnectOAuth: async (data: { password: string }) => {
      await usersApi.disconnectOAuth(data)
    },
    deleteAccount: async (data: { password: string }) => {
      await usersApi.deleteAccount(data)
    },
    leaveProduct: async (productSlug: string) => {
      const response = await usersApi.leaveProduct(productSlug)
      // Map to match published saas-shell expected type
      return {
        message: response.message,
        teams_deleted: response.teams_scheduled_for_deletion,
        teams_left: response.teams_left,
      }
    },
    deactivateAccount: async (data: { soft_delete_at: string }) => {
      await usersApi.deactivateAccount(data)
    },
    cancelDeactivation: async () => {
      await usersApi.cancelDeactivation()
    },
    // Avatar
    uploadAvatar: usersApi.uploadAvatar,
    deleteAvatar: async () => {
      await usersApi.deleteAvatar()
    },
    // 2FA
    setup2FA: usersApi.setup2FA,
    enable2FA: usersApi.enable2FA,
    disable2FA: async (data: { code: string; password?: string }) => {
      await usersApi.disable2FA(data)
    },
    regenerateBackupCodes: usersApi.regenerateBackupCodes,
    // API Keys
    listApiKeys: usersApi.listApiKeys,
    createApiKey: usersApi.createApiKey,
    revokeApiKey: usersApi.revokeApiKey,
    // Billing
    getUsage: billingApi.getUsage,
    getSubscription: billingApi.getSubscription,
    getPortalUrl: billingApi.getPortalUrl,
    cancelSubscription: async (tId: string) => {
      await billingApi.cancelSubscription(tId)
    },
    // Teams
    getTeam: teamsApi.getTeam,
    updateTeam: teamsApi.updateTeam,
    deleteTeam: async (tId: string) => {
      await teamsApi.deleteTeam(tId)
    },
    leaveTeam: teamsApi.leaveTeam,
    getMembers: teamsApi.getMembers,
    transferOwnership: async (tId: string, data: { new_owner_id: string }) => {
      await teamsApi.transferOwnership(tId, data)
    },
    getPendingTransfer: teamsApi.getPendingTransfer,
    cancelPendingTransfer: async (tId: string) => {
      await teamsApi.cancelPendingTransfer(tId)
    },
    resendTransferEmail: async (tId: string) => {
      await teamsApi.resendTransferEmail(tId)
    },
    uploadTeamLogo: usersApi.uploadTeamLogo,
    deleteTeamLogo: async (tId: string) => {
      await usersApi.deleteTeamLogo(tId)
    },
    // Auth
    forgotPassword: async (email: string) => {
      await authApi.forgotPassword(email)
    },
    logout: (redirectTo?: string) => {
      logout(redirectTo)
    },
    // Consent
    updateConsent,
    // Email Change
    requestEmailChange: usersApi.requestEmailChange,
  }

  return (
    <AccountSettingsPage
      teamId={teamId}
      user={user}
      productSlug="falcon"
      api={api as unknown as AccountSettingsPageProps['api']}
      planUsageCard={props => <PlanUsageCard {...props} />}
      transformUsage={
        transformFalconUsage as (
          usageData: unknown,
          subscription: unknown
        ) => ReturnType<typeof transformFalconUsage>
      }
      pricingPath="/pricing"
      onToast={(message, type) => {
        if (type === 'success') toast.success(message)
        else toast.error(message)
      }}
    />
  )
}
